'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadedFile } from '@/types/chat';
import { apiClient } from '@/lib/api-client';
import { reconcileUploadStatuses } from '@/lib/files/reconcile-upload-status';

/**
 * Session-scoped uploaded-files state.
 *
 * uploadedFiles is conversation data living in the unkeyed WorkspaceProvider;
 * it must reset on EVERY session switch (not just the handler paths that
 * remember to scrub it), and a restore arriving from a previous session's
 * async blob load must never land in the current session's list.
 *
 * The reset happens during render (the React "adjust state when props
 * change" pattern) so no frame pairs the new sessionId with the old
 * session's files — autosave and the chat prompt both read that pair.
 */
export function useSessionScopedUploads(sessionId) {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [uploadedFiles, setUploadedFiles] = useState(t0);
  const [prevSessionId, setPrevSessionId] = useState(sessionId);
  const sessionIdRef = useRef(sessionId);
  let t1;
  let t2;
  if ($[1] !== sessionId) {
    t1 = () => {
      sessionIdRef.current = sessionId;
    };
    t2 = [sessionId];
    $[1] = sessionId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (sessionId !== prevSessionId) {
    setPrevSessionId(sessionId);
    setUploadedFiles([]);
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (files, forSessionId) => {
      if (forSessionId !== sessionIdRef.current) {
        return;
      }
      setUploadedFiles(prev => prev.length === 0 ? files : prev);
      const needsReconcile = files.some(_temp);
      if (!needsReconcile) {
        return;
      }
      const restoredIds = new Set(files.map(_temp2));
      reconcileUploadStatuses(files, _temp3).then(reconciled => {
        if (forSessionId !== sessionIdRef.current) {
          return;
        }
        const byId = new Map(reconciled.map(_temp4));
        setUploadedFiles(prev_0 => prev_0.flatMap(p => {
          if (!restoredIds.has(p.id)) {
            return [p];
          }
          const r = byId.get(p.id);
          return r ? [r] : [];
        }));
      }).catch(_temp5);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const restoreFiles = t3;
  let t4;
  if ($[5] !== uploadedFiles) {
    t4 = {
      uploadedFiles,
      setUploadedFiles,
      restoreFiles
    };
    $[5] = uploadedFiles;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp5() {}
function _temp4(f_1) {
  return [f_1.id, f_1];
}
function _temp3(id) {
  return apiClient.getUploadStatus(id);
}
function _temp2(f_0) {
  return f_0.id;
}
function _temp(f) {
  return f.status === "processing" || f.status === "error" && !(f as {
    file_id?: string;
  }).file_id;
}